import { ReactNode, useState, useEffect } from 'react';
import useUnleashFeatureFlagAdapter from '../../adapter/UnleashFeatureFlagAdapter';

export type UseUserFeatureFlagResult = {
  handleRenderByFeatureFlag: (
    component: ReactNode,
    defaultComponent: ReactNode,
  ) => ReactNode;
  isFeatureFlagEnable: boolean;
  flagsReady: boolean;
  /**
   * A avaliação desta flag já voltou (ligada, desligada ou com erro). Antes
   * disso `isFeatureFlagEnable` é `false` por padrão, não por decisão — quem
   * troca de tela pela flag precisa esperar isto, senão mostra a tela errada
   * e troca depois.
   */
  flagResolved: boolean;
};

const useUserFeatureFlag = (featureFlag?: string): UseUserFeatureFlagResult => {
  const { isUserEnabled, flagsReady } = useUnleashFeatureFlagAdapter();
  const [isFeatureFlagEnable, setIsFeatureFlagEnable] = useState(false);
  const [flagResolved, setFlagResolved] = useState(false);

  useEffect(() => {
    let mounted = true;
    setFlagResolved(false);

    const checkFlag = async () => {
      try {
        const enabled = await isUserEnabled(featureFlag!);
        if (mounted) setIsFeatureFlagEnable(enabled);
      } catch (error) {
        console.error(
          'Failed to check user feature flag:',
          featureFlag,
          error,
        );
        if (mounted) setIsFeatureFlagEnable(false);
      } finally {
        if (mounted) setFlagResolved(true);
      }
    };

    if (featureFlag) {
      void checkFlag();
    } else {
      // Sem flag não há o que avaliar: resolvido, e desligado.
      setIsFeatureFlagEnable(false);
      setFlagResolved(true);
    }

    return () => {
      mounted = false;
    };
  }, [featureFlag, isUserEnabled]);

  const handleRenderByFeatureFlag = (
    component: ReactNode,
    defaultComponent: ReactNode,
  ): ReactNode => {
    return isFeatureFlagEnable ? component : defaultComponent;
  };

  return {
    handleRenderByFeatureFlag,
    isFeatureFlagEnable,
    flagsReady,
    flagResolved,
  };
};

export default useUserFeatureFlag;
